import type { PermissionGroupId } from '@strength/domain';
import { useTranslation } from 'react-i18next';
import { useViewer } from '../../context/viewer';

/**
 * Single source of truth for the Teams page tabs — both the nav links
 * (`useTeamTabs`) and the route guards (`tabGuard`, SC-4792) read from
 * this list, so they cannot disagree. Each tab is governed by its
 * own Teams-area View permission (SC-4379). Benchmarks and Settings
 * are single combined View/Create/Edit keys, so a revoked key hides the
 * tab outright rather than partially disabling it. Legacy roles resolve to
 * always-granted, so they see every tab.
 */
export const TEAM_TABS = [
  {
    to: '/$teamId/team/profiles' as const,
    labelKey: 'profiles',
    permissionAccess: 'teams.profiles.view',
  },
  {
    to: '/$teamId/team/benchmarks' as const,
    labelKey: 'benchmarks',
    permissionAccess: 'teams.benchmarks.view',
  },
  {
    to: '/$teamId/team/activity-log' as const,
    labelKey: 'activityLog',
    permissionAccess: 'teams.activityLog.view',
  },
  {
    to: '/$teamId/team/settings' as const,
    labelKey: 'settings',
    permissionAccess: 'teams.settings.view',
  },
] as const;

/**
 * The Teams tabs the current viewer has access to, resolved through the
 * viewer's permissions. Returns links ready for `PageHeader`'s
 * navigationLinks (with translated labels) in display order.
 */
export function useTeamTabs() {
  const { t } = useTranslation();
  const { hasPermissions } = useViewer();

  return TEAM_TABS.filter(({ permissionAccess }) => {
    // permissionAccess is the full dotted key — e.g. 'teams.profiles.view'
    const group = permissionAccess.slice(
      0,
      permissionAccess.lastIndexOf('.'),
    ) as PermissionGroupId;
    const action = permissionAccess.slice(
      permissionAccess.lastIndexOf('.') + 1,
    ) as 'view';
    return hasPermissions(group, action);
  }).map(({ labelKey, ...tab }) => ({
    ...tab,
    label: t(labelKey),
  }));
}
